import React, { useCallback, useMemo, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ExtendedLineItem as CartLineItem } from 'shared/types/cart/LineItem';
import Image from 'components/commercetools-ui/atoms/image';
import QuantitySelector from 'components/commercetools-ui/atoms/quantity-selector';
import configLineItemExtractor from 'helpers/configLineItemExtractor/configLineItemExtractor';
import useClassNames from 'helpers/hooks/useClassNames';
import ItemPrice from './item-price';

interface Props {
  item: CartLineItem;
  onRemoveItem(): Promise<void>;
  onUpdateItem(quantity: number): Promise<void>;
}

const CartItem: React.FC<Props> = ({ item, onRemoveItem, onUpdateItem }) => {
  const [processing, setProcessing] = useState(false);

  const deleteButtonClassName = useClassNames(['block', processing ? 'cursor-not-allowed' : 'cursor-pointer']);

  const handleUpdateItem = useCallback(
    async (quantity: number) => {
      if (processing) return;

      setProcessing(true);
      await (quantity < 1 ? onRemoveItem() : onUpdateItem(quantity));
      setProcessing(false);
    },
    [onUpdateItem, onRemoveItem, processing],
  );

  const handleRemoveItem = useCallback(async () => {
    if (processing) return;

    setProcessing(true);
    await onRemoveItem();
    setProcessing(false);
  }, [onRemoveItem, processing]);

  const config = useMemo(() => configLineItemExtractor(item), [item]);

  return (
    <div className="flex max-w-full items-stretch justify-start gap-16 py-16">
      <div className="shrink-0 bg-white">
        <div className="relative size-100 rounded-sm">
          <Image
            src={item.variant?.images?.[0]}
            /*
            TODO: Commenting this for now so images show up. We need to implement the right Cloudinary strat. eventually
            suffix="small"
            */
            style={{ objectFit: 'contain' }}
            fill
          />
        </div>
      </div>
      <div className="flex max-w-full grow flex-col justify-between gap-16 overflow-hidden">
        <div>
          <div className="flex items-center justify-between">
            <p
              className="body-text max-w-[85%] overflow-hidden text-ellipsis whitespace-pre font-medium uppercase leading-6"
              title={item.name}
            >
              {item.name}
            </p>
            <button data-testid="remove-button" onClick={handleRemoveItem} className={deleteButtonClassName}>
              <TrashIcon className="size-20" />
            </button>
          </div>
          {config && <p className="body-text-x-small">{config}</p>}

          <ItemPrice item={item} />
        </div>

        <div>
          <QuantitySelector
            value={item.count}
            defaultValue={1}
            minValue={1}
            disabled={processing}
            onChange={handleUpdateItem}
          />
        </div>
      </div>
    </div>
  );
};

export default CartItem;
